import { createContext, useContext, useEffect, useState } from "react";
import i18n from "../i18n";
import api from "./api";

const AuthContext = createContext(null);

const LS_REAL_USER = "delphi_user";
const LS_IMPERSONATE_ID = "delphi_impersonate_id";
const LS_IMPERSONATE_USER = "delphi_impersonate_user";

export const AuthProvider = ({ children }) => {
  // `realUser` = the CEO in his own session. `user` = who we're viewing as
  // (either the real user, or the impersonated member when CEO uses "View as").
  const [realUser, setRealUser] = useState(null);
  const [impersonatedUser, setImpersonatedUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    try {
      const stored = localStorage.getItem(LS_REAL_USER);
      if (stored) setRealUser(JSON.parse(stored));
      const impStored = localStorage.getItem(LS_IMPERSONATE_USER);
      if (impStored) setImpersonatedUser(JSON.parse(impStored));
    } catch {
      // Ignore malformed localStorage; user will be treated as logged out.
    }
    setLoading(false);
  }, []);

  const requestMagicLink = async (email) => {
    const { data } = await api.post("/auth/magic-link/request", { email });
    return data;
  };

  const verifyMagicLink = async (token) => {
    const { data } = await api.post("/auth/magic-link/verify", { token });
    localStorage.setItem("delphi_token", data.token);
    const u = { ...data.member, role: data.role, role_label_fr: data.role_label_fr };
    localStorage.setItem(LS_REAL_USER, JSON.stringify(u));
    if (data.preferred_locale && !localStorage.getItem("delphi_user_picked_lang")) {
      localStorage.setItem("delphi.locale", data.preferred_locale);
      i18n.changeLanguage(data.preferred_locale);
    }
    setRealUser(u);
    return u;
  };

  const logout = () => {
    localStorage.removeItem("delphi_token");
    localStorage.removeItem(LS_REAL_USER);
    localStorage.removeItem("delphi_user_picked_lang");
    localStorage.removeItem(LS_IMPERSONATE_ID);
    localStorage.removeItem(LS_IMPERSONATE_USER);
    setRealUser(null);
    setImpersonatedUser(null);
  };

  // CEO-only helpers : the header dropdown wires directly to these.
  const impersonateAs = (candidate) => {
    // `candidate` = shape returned by /admin/impersonate/candidates
    const asUser = {
      id: candidate.id,
      email: candidate.email,
      full_name: candidate.full_name,
      role: candidate.role,
      role_label_fr: candidate.role_label_fr,
      role_label_en: candidate.role_label_en,
      groupe_id: candidate.groupe_id,
      region: candidate.region,
    };
    localStorage.setItem(LS_IMPERSONATE_ID, candidate.id);
    localStorage.setItem(LS_IMPERSONATE_USER, JSON.stringify(asUser));
    setImpersonatedUser(asUser);
    // Force a full reload so every page re-fetches its data with the new scope.
    window.location.href = "/";
  };

  const stopImpersonating = () => {
    localStorage.removeItem(LS_IMPERSONATE_ID);
    localStorage.removeItem(LS_IMPERSONATE_USER);
    setImpersonatedUser(null);
    window.location.href = "/";
  };

  // `user` = who the app should behave as (impersonated if any, else real).
  const user = impersonatedUser || realUser;
  const impersonating = Boolean(impersonatedUser);

  return (
    <AuthContext.Provider value={{
      user,
      realUser,
      impersonating,
      loading,
      requestMagicLink,
      verifyMagicLink,
      logout,
      impersonateAs,
      stopImpersonating,
    }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);
